import React, { useState, useEffect } from 'react';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import { Autocomplete, IconButton } from '@mui/material';
import { useFormik } from 'formik';
import DeleteIcon from '@mui/icons-material/Delete';
import { red } from '@mui/material/colors';
import TableContainer from '@mui/material/TableContainer';
import TableCell from '@mui/material/TableCell';
import Paper from '@mui/material/Paper';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import FormControl from '@mui/material/FormControl';
import { DatePicker } from '@mui/x-date-pickers';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';

import {
  createPMI,
  updatePMI,
  getPMIById,
  getAutoCertificateNo,
  getPMIPDF,
} from '../../../lib/api-pmi';
import { getAllCustomersAuto } from '../../../lib/api-customer';
import { getPmiGradeOptions, getElementsForGrade } from '../../../utils/pmiGrades';
import { APP_BASE_PATH } from '../../../lib/api-base-paths';
// BUG 3: shared handler so pressing Enter inside the sample-entry fields
// (Total Samples / Sample No / element value inputs) never triggers the
// surrounding <form>'s submit (which would save + navigate away).
const preventEnterSubmit = (e) => {
  if (e.key === 'Enter') {
    e.preventDefault();
  }
};

function CreatePMI({ PMIId, isEdit }) {
  const navigate = useNavigate();
  const handleBack = () => navigate('/PMI');

  const [cust, setCust] = useState([]);
  const gradeOptions = getPmiGradeOptions();

  // Dynamic chemical-element columns, derived from the selected grade
  // (or reconstructed from stored data when editing a custom grade).
  const [elements, setElements] = useState([]);
  useEffect(() => {
  console.log("Current Elements State:", elements);
}, [elements]);

  // Material description rows, each with its own list of measured samples.
  const [rows, setRows] = useState([]);
  const [rowIdCounter, setRowIdCounter] = useState(1);

  const [materialForm, setMaterialForm] = useState({
    UN_No: '',
    Desciption: '',
    Qty: '',
    QtyKg: '',
    TotalSamples: ''
});
  const [sampleForms, setSampleForms] = useState({}); // { [rowId]: { values: {symbol: value} } }
  const [sampleCounter, setSampleCounter] = useState({});
const [sampleLimit, setSampleLimit] = useState({});
const [editingSample, setEditingSample] = useState({});
  const [loading, setLoading] = useState(false);

  const formik = useFormik({
initialValues: {
  Cust_Name: { value: '', label: '' },
  Referance_Id: '',
  Cert_Date: dayjs(),
  Certificate_No: '',
  TC_No: '',
  Spc_Grade: '',

  // Default PMI values
  Remark: 'Above Material Confirm To SS304 Grade.',
  Location: 'Bandekar & Sons',
  Instrument: 'Thermo Scientific (Niton) Model XL2 800 Serial No.93346',
  Calibratrion: 'Tii/CAL/SNDT/11359',
  Mode: 'Alloy Analyzer Calibration procedure no.DOC-PRO Tii/06/12.',
  Std: 'ARMI Certificate no 35KN-10112011-LARM-9',
  Tested_By: 'Balram Mohite',
  Witness_By: '',
},
    onSubmit: async (values) => {
      const payload = {
        ...values,
        Cert_Date: values.Cert_Date ? dayjs(values.Cert_Date).format('DD/MM/YYYY') : '',
        rows: rows.map((row) => ({
          srNo: row.srNo,
          UN_No: row.UN_No,
          Desciption: row.Desciption,
          Qty: row.Qty,
          Weight: row.QtyKg || row.Weight || '',
          samples: row.samples,
        })),
      };

      try {
        setLoading(true);
        if (isEdit) {
          await updatePMI(PMIId, payload);
        } else {
          await createPMI(payload);
        }
        navigate('/PMI');
      } catch (error) {
        console.error('Error saving PMI:', error);
        alert('Error saving PMI certificate. Please check the form and try again.');
      } finally {
        setLoading(false);
      }
    },
  });

  useEffect(() => {
    const fetchCustomers = async () => {
      try {
        const response = await getAllCustomersAuto();
        setCust(response.map((row) => ({ value: row.Cust_Id, label: row.Cust_Name })));
      } catch (error) {
        console.error('Error fetching customers:', error);
      }
    };
    fetchCustomers();
  }, []);

  useEffect(() => {
    if (!isEdit) {
      const fetchCertNo = async () => {
        try {
          const response = await getAutoCertificateNo();
          formik.setFieldValue('Certificate_No', response.data.nextCertificateNo);
        } catch (error) {
          console.error('Error fetching Certificate No:', error);
        }
      };
      fetchCertNo();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEdit]);

  useEffect(() => {
    if (!isEdit || !PMIId) return;
    const fetchData = async () => {
      try {
        setLoading(true);
        const response = await getPMIById(PMIId);
        const { pmi, rows: pmiRows } = response.data;

        const parseDate = (dateString) => {
          if (!dateString) return dayjs();
          const parts = dateString.split('/');
          return dayjs(`${parts[2]}-${parts[1]}-${parts[0]}`);
        };

        const custMatch = cust.find((c) => c.value === pmi.Cust_Id) || {
          value: pmi.Cust_Id,
          label: pmi.Customer_Name || pmi.Cust_Name,
        };

        formik.setValues({
          Cust_Name: custMatch,
          Referance_Id: pmi.Referance_Id || '',
          Cert_Date: parseDate(pmi.Cert_Date),
          Certificate_No: pmi.Certificate_No,
          TC_No: pmi.TC_No || '',
          Spc_Grade: pmi.Spc_Grade || '',
          Location: pmi.Location || '',
          Instrument: pmi.Instrument || '',
          Calibratrion: pmi.Calibratrion || '',
          Mode: pmi.Mode || '',
          Std: pmi.Std || '',
          Tested_By: pmi.Tested_By || '',
          Witness_By: pmi.Witness_By || '',
          Remark: pmi.Remark || '',
        });

        setRows(
          (pmiRows || []).map((row) => ({
            id: row.descId,
            srNo: row.srNo,
            UN_No: row.UN_No,
            Desciption: row.Desciption,
            Qty: row.Qty,
            QtyKg: row.Weight || '',
            samples: row.samples || [],
          }))
        );

        // FIX 2: restore the per-row sample counter (and limit) from the
        // samples already saved on this certificate, so the next sample
        // added to a row continues numbering instead of restarting at 1.
        // e.g. a row with 3 saved samples ("Sample 1".."Sample 3") should
        // produce "Sample 4" next; a row with 5 saved samples should
        // produce "Sample 6" next.
        //
        // BUG 1: the Total Samples limit must also be restored to reflect
        // the actual number of samples already on the row (not reset to
        // an empty string), so the limit keeps being enforced after
        // reopening the certificate for editing.
        const restoredCounters = {};
        const restoredLimits = {};
        (pmiRows || []).forEach((row) => {
          let maxSampleNo = 0;
          (row.samples || []).forEach((s) => {
            const match = /(\d+)\s*$/.exec(s.sampleName || '');
            if (match) maxSampleNo = Math.max(maxSampleNo, parseInt(match[1], 10));
          });
          restoredCounters[row.descId] = maxSampleNo + 1;
          restoredLimits[row.descId] = (row.samples || []).length;
        });
        setSampleCounter(restoredCounters);
        setSampleLimit(restoredLimits);

        // Reconstruct the dynamic column list from stored readings so a
        // custom / freeSolo grade still displays correctly on edit.
        const gradeElements = getElementsForGrade(pmi.Spc_Grade);
        if (gradeElements.length > 0) {
          setElements(gradeElements);
        } else {
          const symbols = new Set();
          (pmiRows || []).forEach((row) =>
            (row.samples || []).forEach((s) => (s.elements || []).forEach((e) => symbols.add(e.symbol)))
          );
          setElements(Array.from(symbols).map((symbol) => ({ symbol, min: '', max: '' })));
        }
      } catch (error) {
        console.error('Error fetching PMI data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEdit, PMIId, cust]);

  const isMSGrade = String(formik.values.Spc_Grade || '').trim().toLowerCase() === 'ms';

  const handleGradeChange = (event, newValue) => {
    const gradeName = typeof newValue === 'string' ? newValue : newValue?.label || newValue || '';
    formik.setFieldValue('Spc_Grade', gradeName);
    setElements(getElementsForGrade(gradeName));
  };

  const handleAddMaterialRow = () => {
    if (!materialForm.Desciption || !materialForm.Qty) {
      alert('Please fill in Material Description and Qty.');
      return;
    }
const newRow = {
    id: rowIdCounter,
    srNo: rowIdCounter,
    UN_No: materialForm.UN_No,
    Desciption: materialForm.Desciption,
    Qty: materialForm.Qty,
    // Store Qty in Kg in the material row so it appears in the table
    // and is included in the payload sent to the backend.
    QtyKg: materialForm.QtyKg,
    totalSamples: Number(materialForm.TotalSamples || 0),
    samples: Array.from(
    { length: Number(materialForm.TotalSamples || 0) },
    (_, index) => ({
        sampleName: `Sample ${index + 1}`,
        elements: elements.map((el) => ({
            symbol: el.symbol,
            obsereved: "",
            minp: el.min || "-",
            maxp: el.max || "-",
        })),
    })
),
};
    setRowIdCounter(rowIdCounter + 1);
    setRows((prev) => [...prev, newRow]);
//     setMaterialForm({
//     UN_No: '',
//     Desciption: '',
//     Qty: '',
//     TotalSamples: ''
// });

    // FIX 4: every new material row starts its own independent sample
    // numbering at "Sample 1" with no limit set.
setSampleCounter((prev) => ({
    ...prev,
    [newRow.id]: 1,
}));
    setSampleLimit((prev) => ({
    ...prev,
    [newRow.id]: Number(materialForm.TotalSamples || 0),
}));
setEditingSample((prev) => ({
    ...prev,
    [newRow.id]: 0,
}));
  };

  const handleDeleteMaterialRow = (id) => {
    setRows((prev) => prev.filter((row) => row.id !== id));
  };

  // FIX 1: this now only ever updates the observed chemical element
  // values for a row's in-progress sample entry. The old sampleName
  // branch has been removed since sample numbers are generated
  // automatically (Sample 1, Sample 2, ...).
  const handleSampleFieldChange = (rowId, field, value) => {
    setSampleForms((prev) => {
      const current = prev[rowId] || { values: {} };
      return { ...prev, [rowId]: { ...current, values: { ...current.values, [field]: value } } };
    });
  };

  const handleAddSample = (rowId) => {

    const form = sampleForms[rowId];

    if (!form) {
        alert("Please enter sample values.");
        return;
    }


    const sampleNo = sampleCounter[rowId] || 1;

    const sampleElements = elements.map((el) => ({
        symbol: el.symbol,
        obsereved: form.values?.[el.symbol] || "",
        minp: el.min || "-",
        maxp: el.max || "-"
    }));

    setRows((prev) =>
        prev.map((r) =>
            r.id === rowId
                ? {
                      ...r,
                      samples: [
                          ...r.samples,
                          {
                              sampleName: `Sample ${sampleNo}`,
                              elements: sampleElements,
                          },
                      ],
                  }
                : r
        )
    );

    setSampleCounter((prev) => ({
        ...prev,
        [rowId]: sampleNo + 1,
    }));

setSampleForms((prev) => ({
    ...prev,
    [rowId]: {
        values: {},
    },
}));


   
};

const handleNewSample = (rowId) => {

    setRows((prev) =>
        prev.map((row) => {

            if (row.id !== rowId) return row;

            const nextSampleNo = row.samples.length + 1;

            return {
                ...row,
                samples: [
                    ...row.samples,
                    {
                        sampleName: `Sample ${nextSampleNo}`,
                        elements: elements.map((el) => ({
                            symbol: el.symbol,
                            obsereved: "",
                            minp: el.min || "-",
                            maxp: el.max || "-",
                        })),
                    },
                ],
            };
        })
    );

    setEditingSample((prev) => ({
        ...prev,
        [rowId]: rows.find(r => r.id === rowId)?.samples.length || 0,
    }));

    setSampleForms((prev) => ({
        ...prev,
        [rowId]: {
            values: {},
        },
    }));
};

  const handleDeleteSample = (rowId, sampleIndex) => {
    setRows((prev) =>
      prev.map((row) =>
        row.id === rowId
          ? { ...row, samples: row.samples.filter((_, idx) => idx !== sampleIndex) }
          : row
      )
    );
  };
const handleUpdateSample = (rowId) => {

  if (editingSample[rowId] == null) {
    alert("Select a sample first.");
    return;
  }

  const form = sampleForms[rowId];

  const sampleElements = elements.map((el) => ({
    symbol: el.symbol,
    obsereved: form.values?.[el.symbol] || "",
    minp: el.min || "-",
    maxp: el.max || "-",
  }));

  setRows((prev) =>
    prev.map((row) => {

      if (row.id !== rowId) return row;

      const samples = [...row.samples];

      samples[editingSample[rowId]] = {
        ...samples[editingSample[rowId]],
        elements: sampleElements,
      };

      return {
        ...row,
        samples,
      };

    })
  );

  setEditingSample((prev) => ({
    ...prev,
    [rowId]: null,
  }));

  setSampleForms((prev) => ({
    ...prev,
    [rowId]: {
      values: {},
    },
  }));

};
const handleResetSample = (rowId) => {

  // FIX 5: Reset only clears the in-progress entry fields / edit
  // selection. It must NOT touch sampleCounter, so numbering continues
  // (e.g. 1, 2, 3, reset, next Add Sample -> 4) and previously added
  // samples are left untouched.
  setEditingSample((prev) => ({
    ...prev,
    [rowId]: null,
  }));

  setSampleForms((prev) => ({
    ...prev,
    [rowId]: {
      values: {},
    },
  }));

};
const handlePrint = async () => {
  if (!isEdit || !PMIId) {
    alert("PMI ID not found.");
    return;
  }

  try {
    setLoading(true);

    console.log("EDIT PAGE - PRINTING PMI DATABASE ID:", PMIId);

    // Same API used by the working PMI list page printer
    const getPDF = await getPMIPDF(PMIId);

    console.log("EDIT PAGE - PDF RESPONSE:", getPDF.data);

    const filepath = `${getPDF?.data?.returnpath}?v=${Date.now()}`;

    if (!filepath) {
      alert("PDF path not received.");
      return;
    }

    // Use the same APP_BASE_PATH as the working PMI list page
    const separator = filepath.includes("?") ? "&" : "?";

    window.open(
      `${APP_BASE_PATH}${filepath}${separator}t=${Date.now()}`,
      "_blank"
    );

  } catch (error) {
    console.error("Error while printing PMI:", error);
    alert("Error generating PMI PDF.");
  } finally {
    setLoading(false);
  }
};

  return (
    <div>
      <h2>{isEdit ? 'Edit PMI' : 'Add PMI'}</h2>
      <form onSubmit={formik.handleSubmit}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: '1rem' }}>
            <Autocomplete
              options={cust}
              value={formik.values.Cust_Name}
              getOptionLabel={(option) => option?.label || ''}
              isOptionEqualToValue={(option, value) => option.value === value.value}
              onChange={(e, newValue) => formik.setFieldValue('Cust_Name', newValue || { value: '', label: '' })}
              renderInput={(params) => <TextField {...params} label="Customer" variant="outlined" />}
            />
            <TextField
              label="Ref No"
              variant="outlined"
              name="Referance_Id"
              value={formik.values.Referance_Id}
              onChange={formik.handleChange}
            />
            <FormControl>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  label="Date"
                  format="DD/MM/YYYY"
                  value={formik.values.Cert_Date}
                  onChange={(value) => formik.setFieldValue('Cert_Date', value)}
                />
              </LocalizationProvider>
            </FormControl>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '1rem' }}>
            <TextField
              label="Certificate No"
              variant="outlined"
              name="Certificate_No"
              value={formik.values.Certificate_No}
              onChange={formik.handleChange}
            />
            <TextField
              label="TC No"
              variant="outlined"
              name="TC_No"
              value={formik.values.TC_No}
              onChange={formik.handleChange}
            />
          <Autocomplete
    freeSolo
    options={gradeOptions}
    value={formik.values.Spc_Grade || ""}
    onChange={handleGradeChange}
    onInputChange={(event, value) => {
        formik.setFieldValue("Spc_Grade", value);
        setElements(getElementsForGrade(value));
    }}
    renderInput={(params) => (
        <TextField
            {...params}
            label="Specified Grade"
        />
    )}
/>
          </div>

          {/* MATERIAL ROW ENTRY */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
            <TextField
              label="UN No"
              variant="outlined"
              value={materialForm.UN_No}
              onChange={(e) => setMaterialForm({ ...materialForm, UN_No: e.target.value })}
            />
            <TextField
              label="Material Description"
              variant="outlined"
              value={materialForm.Desciption}
              onChange={(e) => setMaterialForm({ ...materialForm, Desciption: e.target.value })}
            />
            <TextField
              label="Qty"
              variant="outlined"
              value={materialForm.Qty}
              onChange={(e) => setMaterialForm({ ...materialForm, Qty: e.target.value })}
            />

            {isMSGrade && (
              <TextField
                label="Qty in Kg"
                variant="outlined"
                value={materialForm.QtyKg}
                onChange={(e) => setMaterialForm({ ...materialForm, QtyKg: e.target.value })}
              />
            )}

            <TextField
    label="Total Samples"
    variant="outlined"
    type="number"
    value={materialForm.TotalSamples}
    onChange={(e) =>
        setMaterialForm({
            ...materialForm,
            TotalSamples: e.target.value,
        })
    }
    style={{ width: 150 }}
/>

            <Button
              style={{ backgroundColor: 'red', color: 'white' }}
              variant="contained"
              onClick={handleAddMaterialRow}
            >
              Add
            </Button>
          </div>
        </div>

        {/* MATERIAL + DYNAMIC ELEMENT TABLE */}
        <TableContainer style={{ marginTop: '30px' }} component={Paper}>
          <Table
  sx={{
    minWidth: 650,
    tableLayout: "fixed",
  }}
  aria-label="pmi material table"
>
            <TableHead sx={{ backgroundColor: '#1976d2' }}>
              <TableRow>
<TableCell style={{ color: "#fff", width: 45 }}>
    Sr No
</TableCell>

<TableCell style={{ color: "#fff", width: 80 }}>
    UN No
</TableCell>

<TableCell style={{ color: "#fff", width: 180 }}>
    Material Description
</TableCell>

<TableCell style={{ color: "#fff", width: 70 }}>
    Qty
</TableCell>
                {isMSGrade && (
                  <TableCell style={{ color: "#fff", width: 90 }} align="center">
                    Qty in Kg
                  </TableCell>
                )}
                {elements.map((el) => (
                  <TableCell
    key={el.symbol}
    align="center"
    style={{
        color: "#fff",
        whiteSpace: "nowrap",
        minWidth: 90,
    }}
>
    <div style={{ fontWeight: "bold" }}>
        {el.symbol}
    </div>

    <div
        style={{
            fontSize: "10px",
            marginTop: 3,
            lineHeight: 1.2,
        }}
    >
        {el.min === "Balance" || el.max === "Balance" ? (
            <>Balance</>
        ) : (
            <>
                Min : {el.min || "-"}
                <br />
                Max : {el.max || "-"}
            </>
        )}
    </div>
</TableCell>
                ))}
           <TableCell
  align="center"
  style={{
    color: "#fff",
    width: 140,
    minWidth: 140,
  }}
>
  Action
</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row) => (
                <React.Fragment key={row.id}>
                  <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                   <TableCell style={{ width: 45 }}>
    {row.srNo}
</TableCell>

<TableCell style={{ width: 80 }}>
    {row.UN_No}
</TableCell>

<TableCell style={{ width: 180 }}>
    {row.Desciption}
</TableCell>

<TableCell style={{ width: 70 }}>
    {row.Qty}
</TableCell>
                  {isMSGrade && (
                    <TableCell style={{ width: 90 }} align="center">
                      {row.QtyKg || ''}
                    </TableCell>
                  )}
                  {elements.map((el) => (
    <TableCell
        key={el.symbol}
        align="center"
        style={{
            width: 90,
            minWidth: 90,
        }}
    />
))}
                    <TableCell
  align="center"
  style={{
    width: 140,
    minWidth: 140,
  }}
>
                      <IconButton aria-label="delete" style={{ color: red[500] }} onClick={() => handleDeleteMaterialRow(row.id)}>
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>

                  {row.samples.map((sample, idx) => (
                    <TableRow
  key={`${row.id}-${idx}`}
  hover
  style={{ cursor: "pointer" }}
  onClick={() => {

    setEditingSample((prev) => ({
      ...prev,
      [row.id]: idx,
    }));

    const values = {};

    sample.elements.forEach((e) => {
      values[e.symbol] = e.obsereved;
    });

    setSampleForms((prev) => ({
      ...prev,
      [row.id]: {
        values,
      },
    }));

  }}
>
                      <TableCell style={{ width: 45 }} />

<TableCell style={{ width: 80 }} />

<TableCell
  style={{
    width: 180,
    paddingLeft: "24px",
  }}
>
  {sample.sampleName}
</TableCell>

<TableCell
  align="center"
  style={{ width: 70 }}
/>
                      {isMSGrade && <TableCell align="center" style={{ width: 90 }} />}
                      {elements.map((el) => {
                        const found = sample.elements.find((e) => e.symbol === el.symbol);
                        return (
  <TableCell
    key={el.symbol}
    align="center"
    style={{
      width: 90,
      minWidth: 90,
    }}
  >
    {found ? found.obsereved : ""}
  </TableCell>
);
                      })}
<TableCell
  align="center"
  style={{
    width: 140,
    minWidth: 140,
  }}
>
                        <IconButton
                          aria-label="delete-sample"
                          style={{ color: red[500] }}
                          onClick={(e) => {
                            // FIX 3: stop the click from bubbling up to the
                            // row's onClick (which would put this sample
                            // into edit mode right as it's being deleted).
                            e.stopPropagation();
                            handleDeleteSample(row.id, idx);
                          }}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}

                  <TableRow>
                    <TableCell />
                    <TableCell />
                    <TableCell colSpan={isMSGrade ? 3 : 2}>
    <div
        style={{
            display: "flex",
            gap: 10,
            alignItems: "center",
        }}
    >
   
<Button
    variant="contained"
    color="primary"
    size="small"
    onClick={() => handleNewSample(row.id)}
    sx={{
        minWidth: 65,
        width: 65,
        height: 40,
        fontSize: "11px",
        lineHeight: 1.1,
        whiteSpace: "pre-line",
        textTransform: "none",
        padding: 0,
    }}
>
    {"New\nSample"}
</Button>
        <TextField
            size="small"
            label="Sample No"
            style={{ width: 150 }}
        value={
    editingSample[row.id] != null
        ? row.samples[editingSample[row.id]]?.sampleName
        : `Sample ${sampleCounter[row.id] || 1}`
}
            InputProps={{
                readOnly: true,
            }}
             InputLabelProps={{
        shrink: true,
    }}
     sx={{
        width: 185,
    }}
            onKeyDown={preventEnterSubmit}
        />
    </div>
</TableCell>
                    {elements.map((el) => (
<TableCell
    key={el.symbol}
    align="center"
    style={{ minWidth: "95px" }}
>
    <TextField
        size="small"
        label={el.symbol}
        value={sampleForms[row.id]?.values?.[el.symbol] || ""}
        onChange={(e) =>
            handleSampleFieldChange(row.id, el.symbol, e.target.value)
        }
        onKeyDown={preventEnterSubmit}
        inputProps={{
            style: {
                textAlign: "center",
            },
        }}
        sx={{
            width: "85px",
        }}
    />
</TableCell>
))}
                    <TableCell
  align="center"
  style={{
    width: 140,
    minWidth: 140,
  }}
>
  <div
    style={{
      display: "flex",
      flexDirection: "column",
      gap: 8,
      minWidth: 130,
    }}
  >
    <Button
      size="small"
      color="success"
      variant="contained"
      onClick={() => handleAddSample(row.id)}
    >
      Add Sample
    </Button>

    <Button
      size="small"
      color="warning"
      variant="contained"
      onClick={() => handleUpdateSample(row.id)}
    >
      Update Sample
    </Button>

    <Button
      size="small"
      color="error"
      variant="contained"
      onClick={() => handleResetSample(row.id)}
    >
      Reset
    </Button>
  </div>
</TableCell>
                  </TableRow>
                </React.Fragment>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '2rem', marginTop: '30px', marginBottom: '30px' }}>
          <TextField label="Location" variant="outlined" name="Location" value={formik.values.Location} onChange={formik.handleChange} />
          <TextField label="Mode" variant="outlined" name="Mode" value={formik.values.Mode} onChange={formik.handleChange} />
          <TextField label="Remark" variant="outlined" name="Remark" value={formik.values.Remark} onChange={formik.handleChange} />
          <TextField label="STD" variant="outlined" name="Std" value={formik.values.Std} onChange={formik.handleChange} />
          <TextField label="Instrument Used" variant="outlined" name="Instrument" value={formik.values.Instrument} onChange={formik.handleChange} />
          <TextField label="Tested By" variant="outlined" name="Tested_By" value={formik.values.Tested_By} onChange={formik.handleChange} />
          <TextField label="Witness By" variant="outlined" name="Witness_By" value={formik.values.Witness_By} onChange={formik.handleChange} />
          <TextField label="Calibration Certificate" variant="outlined" name="Calibratrion" value={formik.values.Calibratrion} onChange={formik.handleChange} />
        </div>
<div
  style={{
    display: "flex",
    gap: "25px",
    marginTop: "20px",
    marginBottom: "20px",
  }}
>
  <label>
    <input type="checkbox" defaultChecked />
    {" "}Print
  </label>

  <label>
    <input type="checkbox" defaultChecked />
    {" "}Send Mail
  </label>
</div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '2%' }}>
          <Button onClick={handleBack} style={{ backgroundColor: 'red', color: 'white' }} variant="contained">
            CANCEL
          </Button>
          {isEdit && (
            <Button onClick={handlePrint} style={{ backgroundColor: '#ff9800', color: 'white' }} variant="contained">
              PRINT
            </Button>
          )}
          <Button type="submit" disabled={loading} style={{ backgroundColor: 'Green', color: 'white' }} variant="contained">
            SAVE
          </Button>
        </div>
      </form>
    </div>
  );
}

export default CreatePMI;